import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListBlogs,
  adminCreateBlog,
  adminUpdateBlog,
  adminDeleteBlog,
  adminGenerateBlog,
  adminMe,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Sparkles,
  LogOut,
  Trash2,
  Pencil,
  Calendar,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { IONA } from "@/constants/site";

const empty = {
  id: null,
  title: "",
  excerpt: "",
  content: "",
  category: "Genel",
  cover_image: "",
  published: true,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editorOpen, setEditorOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [saving, setSaving] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("iona_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    adminMe()
      .then((res) => setAdminEmail(res.email))
      .catch(() => {
        localStorage.removeItem("iona_admin_token");
        navigate("/admin/login");
      });
    refresh();
  }, [navigate]);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await adminListBlogs();
      setBlogs(data);
    } catch {
      toast.error("Bloglar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("iona_admin_token");
    navigate("/admin/login");
  };

  const openCreate = () => {
    setForm(empty);
    setAiTopic("");
    setEditorOpen(true);
  };

  const openEdit = (b) => {
    setForm({ ...b });
    setAiTopic("");
    setEditorOpen(true);
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!aiTopic.trim()) {
      toast.error("Lütfen bir konu girin");
      return;
    }
    setGenerating(true);
    try {
      const res = await adminGenerateBlog(aiTopic, form.category || "Genel");
      setForm((f) => ({
        ...f,
        title: res.title || f.title,
        excerpt: res.excerpt || f.excerpt,
        content: res.content || f.content,
      }));
      toast.success("AI içerik üretildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI üretim hatası");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Başlık ve içerik zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt || form.title,
        content: form.content,
        category: form.category || "Genel",
        cover_image: form.cover_image || null,
        published: form.published,
      };
      if (form.id) {
        await adminUpdateBlog(form.id, payload);
        toast.success("Blog güncellendi");
      } else {
        await adminCreateBlog(payload);
        toast.success("Blog yayınlandı");
      }
      setEditorOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt hatası");
    } finally {
      setSaving(false);
    }
  };

  const del = async (b) => {
    try {
      await adminDeleteBlog(b.id);
      toast.success("Blog silindi");
      refresh();
    } catch {
      toast.error("Silme hatası");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-30">
        <div className="iona-container flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#5E2A84] flex items-center justify-center overflow-hidden p-1.5">
              <img src={IONA.logo} alt="İONA" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-serif text-lg text-[#1A1A1A]">Admin Panel</div>
              <div className="text-xs text-[#4A4A4A]">{adminEmail}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => window.open("/blog", "_blank")}
              data-testid="admin-view-site"
              className="rounded-full border-[#E5E5E5] hidden sm:inline-flex"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Siteyi Gör
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full border-[#E5E5E5]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="iona-container py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-[#1A1A1A] mb-1">Bloglar</h1>
            <p className="text-sm text-[#4A4A4A]">
              {blogs.length} yazı · AI destekli içerik üretimi mevcuttur
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="admin-new-blog-btn"
            className="rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white h-12 px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Blog
          </Button>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-64 bg-white rounded-3xl animate-pulse border border-[#F0F0F0]"
              />
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center border border-[#F0F0F0]">
            <p className="text-[#4A4A4A] mb-6">Henüz blog yazısı yok.</p>
            <Button
              onClick={openCreate}
              className="rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              İlk Yazıyı Oluştur
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-blog-${b.id}`}
                className="bg-white rounded-3xl overflow-hidden border border-[#F0F0F0] flex flex-col"
              >
                {b.cover_image && (
                  <div className="h-40 overflow-hidden">
                    <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-xs text-[#4A4A4A] mb-3">
                    <span className="px-2.5 py-0.5 rounded-full bg-[#F4F1F9] text-[#5E2A84] uppercase tracking-wider">
                      {b.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(b.created_at).toLocaleDateString("tr-TR")}
                    </span>
                    {!b.published && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] uppercase">
                        Taslak
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-lg text-[#1A1A1A] mb-2 line-clamp-2">
                    {b.title}
                  </h3>
                  <p className="text-xs text-[#4A4A4A] line-clamp-3 mb-5">{b.excerpt}</p>

                  <div className="mt-auto flex gap-2">
                    <Button
                      onClick={() => openEdit(b)}
                      data-testid={`admin-edit-${b.id}`}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#E5E5E5] flex-1"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      Düzenle
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid={`admin-delete-${b.id}`}
                          variant="outline"
                          size="sm"
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Bu blogu silmek istediğinize emin misiniz?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{b.title}" başlıklı yazı kalıcı olarak silinecek.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>İptal</AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`admin-confirm-delete-${b.id}`}
                            onClick={() => del(b)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Sil
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {form.id ? "Blogu Düzenle" : "Yeni Blog Yazısı"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* AI Generator */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#F4F1F9] to-white border border-[#E8E2F0]">
              <div className="flex items-center gap-2 mb-3 text-[#5E2A84]">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI ile İçerik Üret</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="ai-topic-input"
                  placeholder="Örn: Lazer epilasyon öncesi dikkat edilmesi gerekenler"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  className="rounded-full h-11 px-5 border-[#E8E2F0] bg-white"
                />
                <Button
                  type="button"
                  onClick={generate}
                  disabled={generating}
                  data-testid="ai-generate-btn"
                  className="rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white h-11 px-6 whitespace-nowrap"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {generating ? "Üretiliyor..." : "Üret"}
                </Button>
              </div>
              <p className="text-xs text-[#4A4A4A] mt-3">
                İçerik üretildikten sonra aşağıdaki alanlarda düzenleyebilirsiniz.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input
                data-testid="blog-title-input"
                value={form.title}
                onChange={(e) => onChange("title", e.target.value)}
                placeholder="Blog başlığı"
                className="h-11 rounded-full px-5 border-[#E5E5E5]"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Input
                  data-testid="blog-category-input"
                  value={form.category}
                  onChange={(e) => onChange("category", e.target.value)}
                  placeholder="Genel"
                  className="h-11 rounded-full px-5 border-[#E5E5E5]"
                />
              </div>
              <div className="space-y-2">
                <Label>Kapak Görseli URL (opsiyonel)</Label>
                <Input
                  data-testid="blog-cover-input"
                  value={form.cover_image || ""}
                  onChange={(e) => onChange("cover_image", e.target.value)}
                  placeholder="https://..."
                  className="h-11 rounded-full px-5 border-[#E5E5E5]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Özet</Label>
              <Textarea
                data-testid="blog-excerpt-input"
                value={form.excerpt}
                onChange={(e) => onChange("excerpt", e.target.value)}
                placeholder="Kısa özet"
                rows={2}
                className="rounded-2xl border-[#E5E5E5] p-4"
              />
            </div>

            <div className="space-y-2">
              <Label>İçerik</Label>
              <Textarea
                data-testid="blog-content-input"
                value={form.content}
                onChange={(e) => onChange("content", e.target.value)}
                placeholder="Yazı içeriği..."
                rows={12}
                className="rounded-2xl border-[#E5E5E5] p-4"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="published"
                data-testid="blog-published-input"
                type="checkbox"
                checked={form.published}
                onChange={(e) => onChange("published", e.target.checked)}
                className="w-4 h-4 accent-[#5E2A84]"
              />
              <Label htmlFor="published" className="cursor-pointer">
                Yayında
              </Label>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditorOpen(false)}
              data-testid="blog-cancel-btn"
              className="rounded-full"
            >
              İptal
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="blog-save-btn"
              className="rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {form.id ? "Güncelle" : "Yayınla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
